import _ from 'lodash';
import * as utils from './utils';
import appStudioUtils from './utils/appStudioUtils';
import createEventFrom from './eventService';
import platformUtils from './platformUtils';
import reporter from './reporter';
import {generateError} from './validations/errors';
import {handleNativeEvent} from './utils/nativeComponentUtils';

const Intent = {
    WIX_CODE_SITE_API: 'WIX_CODE_SITE_API',
    WIX_CODE_APP_API: 'WIX_CODE_APP_API',
    WIX_CODE_RESPONSE: 'WIX_CODE_RESPONSE',
    WIX_CODE: 'WIX_CODE',
    WIX_CODE_WARMUP: 'WIX_CODE_WARMUP',
    BROWSER_API: 'BROWSER_API'
};

const MessageTypes = {
    NAVIGATE_TO: 'navigateTo',
    SITE_INFO: 'siteInfo',
    SM_CURRENT_USER_EMAIL: 'smCurrentUserEmail',
    SM_CURRENT_USER_SLUG: 'smCurrentUserSlug',
    SM_REQUEST_LOGIN: 'smRequestLogin',
    SM_LOGOUT: 'smLogout',
    SM_FORGOT_PASSWORD: 'smForgotPassword',
    SM_REGISTER_USER: 'smRegisterUser',
    SM_APPLY_SESSION_TOKEN: 'smApplySessionToken',
    ML_SET_LANGUAGECODE: 'mlSetLanguageCode',
    WINDOW_BOUNDING_RECT: 'windowBoundingRect',
    COPY_TO_CLIPBOARD: 'copyToClipboard',
    SCROLL_TO: 'scrollTo',
    SCROLL_TO_COMP: 'scrollToComponent',
    SCROLL_BY: 'scrollBy',
    GET_APP_API: 'getAppAPI',
    INVOKE_APP_FN: 'invokeAppFn',
    INVOKE_APP_ON: 'invokeAppOn',
    SET_TO_STORAGE: 'setToStorage',
    STORAGE_UPDATE: 'storageWasUpdated',
    POST_HTML_MESSAGE: 'postHtmlMessage',
    OPEN_LIGHTBOX: 'openLightbox',
    CLOSE_LIGHTBOX: 'closeLightbox',
    START_UPLOAD: 'startFileUpload',
    GET_ROUTERS_SITEMAP: 'getRoutersSiteMap',
    GET_CURRENT_GEOLOCATION: 'getCurrentGeolocation',
    SET_WARMUP_DATA: 'setWarmupData',
    GOOGLE_ANALYTICS: 'googleAnalytics',
    TRACK_EVENT: 'trackEvent',
    RESET_BEHAVIORS: 'resetBehaviors',
    OPEN_MODAL: 'openModal',
    SEND_MESSAGE_TO_PARENT: 'sendMessageToParent',
    OPEN_MESSAGE_CHANNEL: 'openMessageChannel',
    OPEN_POPUP: 'openPopup',
    OPEN_PERSISTENT_POPUP: 'openPersistentPopup',
    GET_SECTION_URL: 'getSectionUrl',
    SET_PAGE_METADATA: 'setPageMetadata',
    GET_COMPONENT_VIEWPORT_STATE: 'getComponentViewportState',
    SET_TPA_NATIVE_COMPONENT_PROPS: 'setTPANativeComponentProps',
    NOTIFY_EVENT_TO_EDITOR_APP: 'notifyEventToEditorApp',
    PREFETCH_PAGE_RESOURCES: 'prefetchPageResources',
    ADD_QUERY_PARAMS: 'addQueryParams',
    REMOVE_QUERY_PARAMS: 'removeQueryParams'
};

let callbackId = 1;

const registeredEventListeners = {};

const callbacks = {};

function postMessage (message, ports) {
    platformUtils.getMessageService().sendMessage(message, ports);
}

const msgSenders = {
    WIX_CODE_WARMUP: sendWarmupCommandMessage,
    WIX_CODE: sendCompCommandMessage,
    BROWSER_API: sendBrowserCommandMessage,
    WIX_CODE_SITE_API: sendGeneralViewerCommandMessage,
    WIX_CODE_APP_API: sendGeneralViewerCommandMessage
};

const EventsCallbacks = {};

//BI note - each message that comes from a component should include controllerId, appDefinitionId is not needed
//BI note - each message that comes from a namespace should include appDefinitionId, controllerId is not needed
function sendMessageToHost (intent, {command, data, contextId, appDefinitionId, controllerId, compId, callback, ports}) {
    utils.reportWixCodeInteraction({controllerId, appDefinitionId});
    (msgSenders[intent] || sendGeneralViewerCommandMessage)(intent, {command, compId, contextId, data, callback, ports});
}

function sendBrowserCommandMessage (intent, {command, data}) {
    if (!command) {
        return;
    }

    postMessage({
        intent,
        type: command,
        data
    });
}

function sendGeneralViewerCommandMessage (intent, {command, data, callback, ports}) {
    if (!command) {
        return;
    }

    const callbackId = getCallbackId();
    if (callback) {
        callbacks[callbackId] = callback;
    }

    const message = {
        callbackId,
        intent,
        type: command,
        contextId: platformUtils.getRGI().getContextId(),
        deviceType: platformUtils.getRGI().getDeviceType() || 'desktop',
        data
    };

    if (_.get(data, 'compId')) {
        message.compId = data.compId;
    }

    postMessage(message, ports);
}

function sendWarmupCommandMessage(intent, {command, data}) {
    intent = 'WIX_CODE';
    try {
        postMessage({
            intent,
            type: 'wix_code_warmup_data',
            deviceType: platformUtils.getRGI().getDeviceType() || 'desktop',
            data,
            command
        });
    } catch(error) {
        reporter.reportSdkError(generateError.error_post_message({error}));
    }
}

function sendCompCommandMessage (intent, {command, compId, contextId, data, callback}) {
    //validations
    const callbackId = getCallbackId();
    if (callback) {
        callbacks[callbackId] = callback;
    }

    postMessage({
        callbackId,
        intent,
        type: 'wix_code_iframe_command',
        deviceType: platformUtils.getRGI().getDeviceType() || 'desktop',
        data,
        compId,
        contextId,
        command
    });
}

function onMessage (event) {
    if (!event || !event.data) {
        return;
    }

    let data = {};
    try {
        if (_.isString(event.data)) {
            data = JSON.parse(event.data);
        } else if (_.isObject(event.data)) {
            data = event.data;
        }
    } catch(e) {
        return;
    }
    switch(data.intent) {
        case 'TPA_NATIVE_EVENT':
            handleNativeEvent(platformUtils.getRMI(), data);
            break;
        case 'WIX_CODE_RESPONSE':
            const callback = data.callbackId && callbacks[data.callbackId];
            if (callback) {
                if (!_.isNil(data.result)) {
                    let dataToPassToCallback = data.result;
                    if(data.type === MessageTypes.INVOKE_APP_ON) {
                        dataToPassToCallback = createEventFrom(data.result);
                    } else if(data.result.command === 'propsChanged') {
                        dataToPassToCallback = undefined;
                    }
                    callback.call(this, dataToPassToCallback, null, event.ports);
                } else {
                    callback(data.result, data.error, event.ports);
                }

                if (data.type !== MessageTypes.INVOKE_APP_ON) {
                    delete callbacks[data.callbackId];
                }
            }
            break;

        case 'addEventListener':
            callEventListeners(data);
            break;

        case 'WIX_CODE':
            const RGI = platformUtils.getRGI();

            if (data.type === 'update_site_member') {
                RGI.addSiteMemberData(data.updates);
                break;
            }

            if (data.type === 'update_apps_data') {
                RGI.addAppsData(data.updates);
                break;
            }

            if (data.type === 'update_navigation') {
                RGI.addNavigation(data.updates);
                break;
            }

            if (data.type === 'update_session_info') {
                RGI.addSessionInfoProp(data.updates);
                break;
            }

            let RMI;
            try {
                RMI = platformUtils.getRMI();
            } catch (e) {
                return; //Ignore events before we have a model in here.
            }

            if (data.type === 'trigger_app_studio_widget_on_props_changed') {
                if (platformUtils.isExperimentOpen('se_appWidgetAsRoot')) {
                    appStudioUtils.setWidgetProperties(data.newProps);
                } else {
                    appStudioUtils.addAppStudioWidgetPropsToSelector(data.newProps);
                    RGI.triggerWidgetPropsChanged(data.oldProps, data.newProps);
                    RMI.updateWidgetProperties(data.newProps);
                }
                break;
            }



            if (data.type === 'update') {
                RMI.updateModel(data.updates);
                break;
            }

            // TODO: should have a check for data.type
            const funcRef = data.callbackId;
            const compId = data.compId;
            const dynamicCallback = RMI.getCallbackById(funcRef, true);
            const staticCallback = platformUtils.getStaticEventHandler(funcRef);
            if (dynamicCallback || staticCallback) {
                addContextToEventData(data, RMI, compId);
            }

            if (dynamicCallback) {
                const selector = platformUtils.getWixSelector().getScopedSelector(compId, dynamicCallback.contextId, dynamicCallback.controllerId);
                dynamicCallback.callback.call(this, createEventFrom(data, dynamicCallback.controllerId), selector);
            }
            if (staticCallback) {
                const selector = platformUtils.getWixSelector().getScopedSelector(compId, staticCallback.contextId, staticCallback.controllerId);
                staticCallback.callback.call(this, createEventFrom(data, staticCallback.controllerId), selector);
            }

            break;
    }

    if (registeredEventListeners[data.type]) {
        registeredEventListeners[data.type](data);
    }
}

function addContextToEventData(data, RMI, compId){
    const context = RMI.getScopeByCompId(compId);

    if (_.get(context, 'additionalData.itemId')) {
        context.itemId = context.additionalData.itemId;
    }

    _.set(data, 'event.context', context);
}

function registerEventListener(msgType, listener) {
    registeredEventListeners[msgType] = listener;
}

function unregisterEventListener(msgType) {
    delete registeredEventListeners[msgType];
}

function callEventListeners(data, typeOfCall){
    if (EventsCallbacks[data.eventType]) {
        EventsCallbacks[data.eventType].forEach(function (callbackHandler) {
            callbackHandler.callback.call(this, data.params, typeOfCall);
        });
    }
}

function getCallbackId () {
    return callbackId++;
}

export {
    sendMessageToHost,
    MessageTypes,
    Intent,
    callEventListeners,
    onMessage,
    registerEventListener,
    unregisterEventListener
};
